import { useStore } from '@xyflow/react'
import { useRef, useCallback } from 'react'

/**
 * Edge topology representation - only the structural data needed for traversal
 */
export interface EdgeTopology {
  source: string
  target: string
}

// Module-level cache to ensure consistent references across all hook instances
let cachedTopology: EdgeTopology[] = []
let cachedSignature = ''

/**
 * Custom hook that provides stable edge topology data that doesn't change during drag.
 *
 * The problem: useEdges() returns a new array reference on every React Flow state change,
 * including position updates during drag. This causes all components using useEdges() to re-render.
 *
 * The solution: This hook uses useStore with a custom equality function that only triggers
 * re-renders when the actual edge topology (source/target pairs) changes, not when
 * positions change during drag.
 *
 * @returns Stable array of edge topology (source/target pairs)
 */
export function useStableEdgeTopology(): EdgeTopology[] {
  // Use a ref to track if we need to update the cache
  const lastSignatureRef = useRef(cachedSignature)

  // Custom selector that extracts topology and uses signature-based equality
  const selector = useCallback((state: { edges: Array<{ source: string; target: string }> }) => {
    const signature = state.edges.map((e) => `${e.source}->${e.target}`).join('|')

    // If signature matches cache, return cached value (stable reference)
    if (signature === cachedSignature) {
      return cachedTopology
    }

    // Topology changed - update cache
    cachedSignature = signature
    cachedTopology = state.edges.map((e) => ({
      source: e.source,
      target: e.target,
    }))

    return cachedTopology
  }, [])

  // Custom equality function - compare by reference since selector returns cached value
  const equalityFn = useCallback((a: EdgeTopology[], b: EdgeTopology[]) => {
    return a === b
  }, [])

  const topology = useStore(selector, equalityFn)

  // Track signature changes for debugging
  if (lastSignatureRef.current !== cachedSignature) {
    lastSignatureRef.current = cachedSignature
  }

  return topology
}
